import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { 
  Package, Plus, TrendingUp, Zap, Loader2, Edit, Trash2, Power,
  Check, MessageSquare, FileText, Users, Crown, Megaphone, AlertTriangle
} from 'lucide-react';
import { HelpButton } from '@/components/common/HelpButton';
import { PricingConfigPanel } from '@/components/admin/PricingConfigPanel';
import { PricingCollisionCard } from '@/components/admin/PricingCollisionCard';
import api from '@/services/api';
import { toast } from 'sonner';

// Plan Card Component
const PlanCard = ({ plan, onEdit, onToggle, onDelete, isRecommended }) => (
  <Card className={`relative overflow-hidden transition-all ${plan.is_active ? 'border-slate-200' : 'border-slate-100 opacity-60'} ${plan.billing_type === 'one_time' ? 'ring-2 ring-amber-300' : ''} ${isRecommended ? 'ring-2 ring-emerald-400 shadow-lg' : ''}`}>
    {isRecommended && (
      <div
        className="absolute -top-px left-1/2 -translate-x-1/2 bg-gradient-to-r from-emerald-500 to-teal-500 text-white text-[10px] font-semibold px-3 py-1 rounded-b-lg shadow uppercase tracking-wider"
        data-testid={`most-popular-badge-${plan.plan_id}`}
      >
        ★ Most Popular
      </div>
    )}
    {plan.is_featured && (
      <div className="absolute top-0 right-0 bg-gradient-to-l from-amber-500 to-amber-400 text-white text-xs font-medium px-3 py-1 rounded-bl-lg">
        Featured
      </div>
    )}
    {plan.billing_type === 'one_time' && (
      <div className="absolute top-0 left-0 bg-gradient-to-r from-amber-500 to-amber-400 text-white text-xs font-medium px-3 py-1 rounded-br-lg flex items-center gap-1">
        <Crown className="w-3 h-3" /> Lifetime
      </div>
    )}
    {plan.show_advertisement && (
      <div className="absolute top-8 left-0 bg-gradient-to-r from-orange-500 to-red-400 text-white text-xs font-medium px-2 py-0.5 rounded-r-lg flex items-center gap-1">
        <Megaphone className="w-3 h-3" /> Promo
      </div>
    )}
    <CardHeader className={`pb-2 ${plan.billing_type === 'one_time' || plan.show_advertisement ? 'pt-8' : ''}`}>
      <div className="flex items-center justify-between">
        <CardTitle className="text-lg text-slate-900">{plan.name}</CardTitle>
        <Badge variant={plan.is_active ? "default" : "secondary"} className={plan.is_active ? "bg-green-100 text-green-700 border-green-200" : ""}>
          {plan.is_active ? 'Active' : 'Inactive'}
        </Badge>
      </div>
      <CardDescription className="text-slate-600 text-sm">{plan.description || 'No description'}</CardDescription>
    </CardHeader>
    <CardContent className="space-y-4">
      <div className="flex items-baseline gap-1">
        {plan.billing_type === 'one_time' ? (
          <>
            <span className="text-3xl font-bold text-amber-600">${plan.one_time_price || 0}</span>
            <span className="text-amber-500 text-sm">one-time</span>
          </>
        ) : (
          <>
            <span className="text-3xl font-bold text-slate-900">${plan.monthly_price || 0}</span>
            <span className="text-slate-500">/month</span>
            {plan.annual_price > 0 && (
              <span className="text-sm text-emerald-600 ml-2">${plan.annual_price}/year</span>
            )}
          </>
        )}
      </div>
      
      <div className="space-y-2 text-sm">
        <div className="flex items-center gap-2 text-slate-600">
          <Users className="w-4 h-4 text-blue-500" />
          <span>{plan.limits?.accounts || plan.account_limit || 1} account(s)</span>
        </div>
        <div className="flex items-center gap-2 text-slate-600">
          <MessageSquare className="w-4 h-4 text-purple-500" />
          <span>{plan.limits?.chatbots || plan.chatbot_limit || 1} chatbot(s)</span>
        </div>
        <div className="flex items-center gap-2 text-slate-600">
          <FileText className="w-4 h-4 text-emerald-500" />
          <span>{plan.limits?.documents || plan.document_limit || 10} documents</span>
        </div>
      </div>
      
      {plan.features?.length > 0 && (
        <div className="pt-2 border-t border-slate-100">
          <p className="text-xs font-medium text-slate-500 mb-2">Features:</p>
          <div className="flex flex-wrap gap-1">
            {plan.features.slice(0, 3).map((f) => (
              <Badge key={f} variant="outline" className="text-xs bg-slate-50">{f}</Badge>
            ))}
            {plan.features.length > 3 && (
              <Badge variant="outline" className="text-xs bg-slate-50">+{plan.features.length - 3} more</Badge>
            )}
          </div>
        </div>
      )}
      
      <div className="flex gap-2 pt-2">
        <Button size="sm" variant="outline" onClick={onEdit} className="flex-1 border-slate-300">
          <Edit className="w-3 h-3 mr-1" /> Edit
        </Button>
        <Button size="sm" variant="outline" onClick={onToggle} className={plan.is_active ? "text-amber-600 border-amber-200 hover:bg-amber-50" : "text-green-600 border-green-200 hover:bg-green-50"}>
          <Power className="w-3 h-3" />
        </Button>
        <Button size="sm" variant="outline" onClick={onDelete} className="text-red-600 border-red-200 hover:bg-red-50">
          <Trash2 className="w-3 h-3" />
        </Button>
      </div>
    </CardContent>
  </Card>
);

export const AdminPlansTab = ({
  plans,
  loadPlans,
  showPlanModal,
  setShowPlanModal,
  editingPlan,
  setEditingPlan,
  suggestedPlanData,
  setSuggestedPlanData,
  targetProfitMargin,
  setTargetProfitMargin,
  smartPricingLoading,
  setSmartPricingLoading,
  smartPricingData,
  setSmartPricingData,
  PlanModal
}) => {

  // Plan Recommendation Tag (iter126): the PricingCollisionCard below
  // dispatches a 'harmonix:recommended-plan' event with the healthiest-margin
  // "good" plan. We badge that plan's card as "Most popular" so admins get a
  // growth nudge without doing the arithmetic.
  const [recommendedPlanId, setRecommendedPlanId] = useState(null);
  useEffect(() => {
    const onRec = (e) => setRecommendedPlanId(e?.detail?.plan_id || null);
    window.addEventListener('harmonix:recommended-plan', onRec);
    return () => window.removeEventListener('harmonix:recommended-plan', onRec);
  }, []);

  const loadSmartPricing = async () => {
    setSmartPricingLoading(true);
    try {
      const response = await api.post('/admin/smart-pricing', {
        target_profit_margin: targetProfitMargin,
        include_projections: true
      });
      setSmartPricingData(response.data);
    } catch (error) {
      toast.error('Failed to calculate smart pricing');
    } finally {
      setSmartPricingLoading(false);
    }
  };

  const handleTogglePlan = async (plan) => {
    // Deactivating a plan with active subscribers requires migrating them
    // first — otherwise those users end up subscribed to an "Inactive" plan.
    if (plan.is_active) {
      try {
        const usersCount = (await api.get(`/admin/plans/${plan.plan_id}/users-count`)).data?.count || 0;
        if (usersCount > 0) {
          setMigrationPlan(plan);
          setMigrationAction('deactivate');
          setMigrationUsersCount(usersCount);
          setTargetPlanId('');
          return;
        }
      } catch (err) {
        // If we can't confirm the subscriber count we must NOT silently
        // deactivate — that could orphan paying users on an inactive plan.
        toast.error(err.response?.data?.detail || 'Could not verify active subscribers — please retry.');
        return;
      }
    }
    try {
      await api.patch(`/admin/plans/${plan.plan_id}`, { is_active: !plan.is_active });
      loadPlans();
      toast.success(`Plan ${plan.is_active ? 'deactivated' : 'activated'}`);
    } catch (e) { 
      toast.error('Failed to update plan'); 
    }
  };

  // Migration modal state (iter130 restore) — when admin tries to delete or
  // deactivate a plan with active subscribers we open this modal and force
  // them to pick a target plan to migrate everyone to first. This restores
  // the workflow that lived in AdminPage.jsx before the tab was extracted.
  const [migrationPlan, setMigrationPlan] = useState(null);
  const [migrationAction, setMigrationAction] = useState('delete'); // 'delete' | 'deactivate'
  const [migrationUsersCount, setMigrationUsersCount] = useState(0);
  const [targetPlanId, setTargetPlanId] = useState('');
  const [migrationLoading, setMigrationLoading] = useState(false);

  const handleDeletePlan = async (plan) => {
    try {
      const usersCount = (await api.get(`/admin/plans/${plan.plan_id}/users-count`)).data?.count || 0;
      if (usersCount > 0) {
        // Open migration modal instead of erroring – matches the product rule
        // "admin can delete any plan, but if there are active users show a
        // migration prompt".
        setMigrationPlan(plan);
        setMigrationAction('delete');
        setMigrationUsersCount(usersCount);
        setTargetPlanId('');
        return;
      }
      if (!window.confirm(`Delete "${plan.name}"? This action cannot be undone.`)) return;
      const res = await api.delete(`/admin/plans/${plan.plan_id}`);
      // Backend returns 200 with {success:false, error:"plan_in_use"} rather
      // than an HTTP error, so we have to inspect the payload explicitly.
      if (res.data && res.data.success === false) {
        toast.error(res.data.message || 'Could not delete plan');
        // Fall back to the migration flow in case users appeared between the
        // count check and the delete call.
        if (res.data.error === 'plan_in_use') {
          setMigrationPlan(plan);
          setMigrationAction('delete');
          setMigrationUsersCount(res.data.users_count || 1);
          setTargetPlanId('');
        }
        return;
      }
      loadPlans();
      toast.success('Plan deleted successfully');
    } catch (e) { 
      toast.error(e.response?.data?.detail || 'Failed to delete plan'); 
    }
  };

  const executeMigration = async () => {
    if (!targetPlanId) {
      toast.error('Please pick a plan to migrate users to');
      return;
    }
    setMigrationLoading(true);
    try {
      await api.post(`/admin/plans/${migrationPlan.plan_id}/migrate-users`, {
        target_plan_id: targetPlanId,
      });
      if (migrationAction === 'delete') {
        await api.delete(`/admin/plans/${migrationPlan.plan_id}`);
        toast.success('Users migrated and plan deleted');
      } else {
        await api.patch(`/admin/plans/${migrationPlan.plan_id}`, { is_active: false });
        toast.success('Users migrated and plan deactivated');
      }
      setMigrationPlan(null);
      setTargetPlanId('');
      loadPlans();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Migration failed');
    } finally {
      setMigrationLoading(false);
    }
  };

  return (
    <>
      <Card className="bg-white border-slate-200 shadow-sm">
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="text-slate-900 flex items-center gap-2">
              <Package className="w-5 h-5 text-blue-600" />Service Plans
            </CardTitle>
            <CardDescription className="text-slate-600">Create and manage subscription plans</CardDescription>
          </div>
          <div className="flex gap-2">
            <HelpButton 
              title="Plans Management" 
              content={`<p>Manage every subscription tier your customers can buy:</p><ul><li><strong>Create:</strong> Add new fixed plans with monthly / annual / one-time pricing, feature flags, and per-plan limits (accounts, chatbots, documents, messages).</li><li><strong>Edit:</strong> Adjust price, description, and limits at any time — changes apply to future subscriptions.</li><li><strong>Activate/Deactivate:</strong> Hide a plan from the pricing page without deleting historical data.</li><li><strong>Custom Plan Pricing:</strong> Below the plans grid, set the profit margin used by the pricing-page slider. Users see the marked-up price only; base cost and margin stay internal.</li><li><strong>Custom vs Fixed analysis:</strong> The bottom card flags whenever a fixed plan is priced the same as (or higher than) what the custom builder would charge for equivalent limits — a warning that the fixed plan may be cannibalising itself.</li></ul>`} 
            />
            <Button onClick={() => { setEditingPlan(null); setShowPlanModal(true); }} className="bg-blue-600 hover:bg-blue-700">
              <Plus className="w-4 h-4 mr-2" />Create Plan
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {plans.length === 0 ? (
              <div className="col-span-full text-center py-12 text-slate-500">
                <Package className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>No plans created yet</p>
                <Button className="mt-4 bg-blue-600 hover:bg-blue-700" onClick={() => setShowPlanModal(true)}>
                  <Plus className="w-4 h-4 mr-2" />Create First Plan
                </Button>
              </div>
            ) : plans.map((plan) => (
              <PlanCard 
                key={plan.plan_id} 
                plan={plan} 
                onEdit={() => { setEditingPlan(plan); setShowPlanModal(true); }} 
                onToggle={() => handleTogglePlan(plan)}
                onDelete={() => handleDeletePlan(plan)}
                isRecommended={plan.plan_id === recommendedPlanId}
              />
            ))}
          </div>
        </CardContent>
      </Card>
      
      {PlanModal && (
        <PlanModal 
          isOpen={showPlanModal} 
          onClose={() => { setShowPlanModal(false); setSuggestedPlanData(null); }} 
          plan={editingPlan} 
          suggestedData={suggestedPlanData} 
          onSave={async (data) => {
            try {
              if (editingPlan) await api.patch(`/admin/plans/${editingPlan.plan_id}`, data);
              else await api.post('/admin/plans', data);
              loadPlans();
              setShowPlanModal(false);
              setSuggestedPlanData(null);
              toast.success(editingPlan ? 'Plan updated' : 'Plan created');
            } catch (e) { toast.error('Failed to save plan'); }
          }} 
        />
      )}

      {/* Custom Plan Pricing Configuration — where admins set profit % */}
      <PricingConfigPanel />

      {/* Custom-vs-Fixed collision analysis (iter125) */}
      <PricingCollisionCard plans={plans} />
      
      {/* Smart Pricing Calculator */}
      <Card className="bg-white border-slate-200 shadow-sm mt-6">
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="text-slate-900 flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-emerald-600" />
              Smart Pricing Calculator
            </CardTitle>
            <CardDescription className="text-slate-600">
              AI-powered pricing suggestions based on usage patterns and costs
            </CardDescription>
          </div>
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <Label className="text-sm text-slate-600">Target Margin:</Label>
              <Input 
                type="number" 
                value={targetProfitMargin} 
                onChange={(e) => setTargetProfitMargin(Number(e.target.value))}
                className="w-20 bg-white border-slate-300 text-slate-900"
                min={0}
              />
              <span className="text-slate-500">%</span>
            </div>
            <Button 
              onClick={loadSmartPricing} 
              disabled={smartPricingLoading}
              className="bg-emerald-600 hover:bg-emerald-700"
            >
              {smartPricingLoading ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Zap className="w-4 h-4 mr-2" />
              )}
              Calculate
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {smartPricingData ? (
            <div className="space-y-4">
              <div className="grid md:grid-cols-3 gap-4">
                <div className="p-4 bg-blue-50 rounded-lg">
                  <p className="text-sm text-blue-600 font-medium">Average Cost/User</p>
                  <p className="text-2xl font-bold text-blue-700">${smartPricingData.avg_cost_per_user?.toFixed(2) || '0.00'}</p>
                </div>
                <div className="p-4 bg-emerald-50 rounded-lg">
                  <p className="text-sm text-emerald-600 font-medium">Suggested Base Price</p>
                  <p className="text-2xl font-bold text-emerald-700">${smartPricingData.suggested_base_price?.toFixed(2) || '0.00'}</p>
                </div>
                <div className="p-4 bg-purple-50 rounded-lg">
                  <p className="text-sm text-purple-600 font-medium">Projected Revenue</p>
                  <p className="text-2xl font-bold text-purple-700">${smartPricingData.projected_monthly_revenue?.toFixed(2) || '0.00'}</p>
                </div>
              </div>
              
              {smartPricingData.tier_suggestions && (
                <div className="mt-4">
                  <h4 className="font-medium text-slate-900 mb-3">Suggested Tier Pricing</h4>
                  <div className="grid md:grid-cols-3 gap-3">
                    {smartPricingData.tier_suggestions.map((tier) => (
                      <div key={tier.name} className="p-3 border border-slate-200 rounded-lg">
                        <p className="font-medium text-slate-900">{tier.name}</p>
                        <p className="text-xl font-bold text-blue-600">${tier.suggested_price}/mo</p>
                        <p className="text-xs text-slate-500 mt-1">{tier.rationale}</p>
                        <Button 
                          size="sm" 
                          variant="outline" 
                          className="mt-2 w-full"
                          onClick={() => {
                            setSuggestedPlanData(tier);
                            setEditingPlan(null);
                            setShowPlanModal(true);
                          }}
                        >
                          Use This
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-8 text-slate-500">
              <Zap className="w-10 h-10 mx-auto mb-3 opacity-50" />
              <p>Click Calculate to get AI-powered pricing suggestions</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* iter130 — Plan migration modal (delete/deactivate with active subs) */}
      <Dialog
        open={!!migrationPlan}
        onOpenChange={(open) => { if (!open) { setMigrationPlan(null); setTargetPlanId(''); } }}
      >
        <DialogContent className="bg-white max-w-md" data-testid="plan-migration-dialog">
          <DialogHeader>
            <DialogTitle className="text-slate-900 flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-500" />
              Migrate users before {migrationAction === 'delete' ? 'deleting' : 'deactivating'}
            </DialogTitle>
            <DialogDescription className="text-slate-600">
              <span className="font-medium text-slate-900">{migrationUsersCount}</span>{' '}
              active user{migrationUsersCount === 1 ? '' : 's'} {migrationUsersCount === 1 ? 'is' : 'are'} still
              subscribed to <span className="font-medium text-slate-900">{`"${migrationPlan?.name}"`}</span>. Pick a plan to
              move them to and we&apos;ll then {migrationAction === 'delete' ? 'delete' : 'deactivate'} the old one.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 pt-2">
            <Label className="text-slate-700">Move users to</Label>
            <Select value={targetPlanId} onValueChange={setTargetPlanId}>
              <SelectTrigger
                className="bg-white border-slate-300"
                data-testid="plan-migration-target-select"
              >
                <SelectValue placeholder="Select target plan" />
              </SelectTrigger>
              <SelectContent className="bg-white">
                {plans
                  .filter((p) => p.is_active && p.plan_id !== migrationPlan?.plan_id)
                  .map((p) => (
                    <SelectItem key={p.plan_id} value={p.plan_id}>
                      {p.name}
                      {typeof p.monthly_price === 'number' && p.monthly_price > 0 && (
                        <span className="text-slate-500 ml-2">· ${p.monthly_price}/mo</span>
                      )}
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => { setMigrationPlan(null); setTargetPlanId(''); }}
              className="border-slate-300"
            >
              Cancel
            </Button>
            <Button
              onClick={executeMigration}
              disabled={!targetPlanId || migrationLoading}
              className={migrationAction === 'delete' ? 'bg-red-600 hover:bg-red-700' : 'bg-amber-600 hover:bg-amber-700'}
              data-testid="plan-migration-confirm"
            >
              {migrationLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Migrate & {migrationAction === 'delete' ? 'delete' : 'deactivate'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default AdminPlansTab;
